import { Box, Checkbox } from "@mui/material";
import DebouncedTextField from "@/components/DebouncedTextField";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { EMPTY_ROW_ENTRY } from "../readmission-f2f-constant";
import { RowTextValue } from "../readmission-f2f-types";
import {
  getBlockedRowKeys,
  getConflictingRowKeys,
} from "../readmission-f2f-utils";

export interface RowDef {
  key: string;
  placeholder?: string;
}

export interface CheckboxRowTextGroupProps {
  rows: RowDef[];
  value: RowTextValue;
  onChange: (value: RowTextValue) => void;
  columns?: number;
  exclusive?: Record<string, string[]>;
}

const CheckboxRowTextGroup = ({
  rows,
  value,
  onChange,
  columns = 1,
  exclusive,
}: CheckboxRowTextGroupProps) => {
  const entryOf = (key: string) => value[key] ?? { checked: false, text: "" };
  const blockedKeys = getBlockedRowKeys(value, exclusive);

  const toggle = (key: string) => {
    const willCheck = !entryOf(key).checked;
    const next: RowTextValue = {
      ...value,
      [key]: willCheck ? { ...entryOf(key), checked: true } : EMPTY_ROW_ENTRY,
    };
    if (willCheck) {
      getConflictingRowKeys(key, exclusive).forEach((other) => {
        next[other] = EMPTY_ROW_ENTRY;
      });
    }
    onChange(next);
  };

  const setText = (key: string, text: string) =>
    onChange({ ...value, [key]: { ...entryOf(key), text } });

  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      gap={1.5}
    >
      {rows.map((row) => {
        const entry = entryOf(row.key);
        const isBlocked = blockedKeys.has(row.key);
        return (
          <Flex key={row.key} gap={1} alignItems="center">
            <Checkbox
              size="small"
              checked={entry.checked}
              disabled={isBlocked}
              onChange={() => toggle(row.key)}
              inputProps={{ "aria-label": row.key }}
            />
            <Text
              sx={{ minWidth: 140 }}
              c={isBlocked ? "text.disabled" : undefined}
            >
              {row.key}
            </Text>
            <DebouncedTextField
              size="small"
              fullWidth
              disabled={!entry.checked || isBlocked}
              placeholder={row.placeholder}
              value={entry.text}
              onChange={(text) => setText(row.key, text)}
            />
          </Flex>
        );
      })}
    </Box>
  );
};

export default CheckboxRowTextGroup;
